import { HTMLProps, ReactNode, forwardRef, useCallback, useContext, useMemo } from 'react'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { useModalPortalContainer } from '../Modal/Modal'
import Portal, { getModalRoot } from '../Portal/Portal'

type PopoverBaseProps = {
  isOpen: boolean
  onOpen?: () => void
  onClose?: () => void
  children?: ReactNode
}
const PopoverBase = ({ isOpen, onOpen, onClose, children }: PopoverBaseProps) => {
  const contextValue: PopoverContextType = useMemo(() => ({ isOpen }), [isOpen])
  return (
    <PopoverPrimitive.Root
      open={isOpen}
      onOpenChange={(newIsOpen) => (newIsOpen ? onOpen?.() : onClose?.())}
    >
      <PopoverContext.Provider value={contextValue}>{children}</PopoverContext.Provider>
    </PopoverPrimitive.Root>
  )
}

const PopoverArrow = forwardRef<SVGSVGElement, HTMLProps<SVGSVGElement>>((props, ref) => {
  return <PopoverPrimitive.Arrow ref={ref} {...props} className="fill-white" />
})

const StyledContent = styled(PopoverPrimitive.Content)<{
  $borderRadius?: React.CSSProperties['borderRadius']
  $boxShadow: React.CSSProperties['boxShadow']
}>`
  max-width: 100vw;
  max-height: var(--radix-popover-content-available-height);
  overflow: auto;
  background-color: ${({ theme }) => theme.colorWhite};
  border-radius: ${({ $borderRadius }) => $borderRadius || '8px'};
  z-index: 1000; /* remove once not competing with antd which styles all of its modal content at z-index 1000 */
`
type PopoverContentProps = HTMLProps<HTMLDivElement> &
  Pick<
    PopoverPrimitive.PopoverContentProps,
    | 'align'
    | 'alignOffset'
    | 'avoidCollisions'
    | 'onOpenAutoFocus'
    | 'onEscapeKeyDown'
    | 'onFocusOutside'
    | 'onInteractOutside'
    | 'side'
    | 'sideOffset'
    | 'collisionBoundary'
  > & {
    borderRadius?: React.CSSProperties['borderRadius']
    boxShadow?: React.CSSProperties['boxShadow']
    children?: React.ReactNode
  }
const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      avoidCollisions: avoidCollisionsProp = true,
      borderRadius,
      boxShadow,
      children,
      onEscapeKeyDown,
      ...rest
    }: PopoverContentProps,
    ref
  ) => {
    const { isOpen } = useContext(PopoverContext)
    const modalPortalContainer = useModalPortalContainer()

    const handleEscapeKeyDown = useCallback(
      (e: KeyboardEvent) => {
        e.stopPropagation()
        onEscapeKeyDown?.(e)
      },
      [onEscapeKeyDown]
    )

    // prevent radix dialog from squashing the wheel event if a popover is inside a modal
    // https://github.com/radix-ui/primitives/issues/1159
    const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
      e.stopPropagation()
    }, [])

    if (!isOpen) {
      return null // Don't mount content if the popover is closed
    }

    return (
      <Portal container={modalPortalContainer ?? getModalRoot()}>
        <StyledContent
          {...rest}
          ref={ref}
          $borderRadius={borderRadius}
          $boxShadow={boxShadow}
          avoidCollisions={avoidCollisionsProp}
          collisionPadding={{ right: 20, bottom: 12, top: 12 }}
          onEscapeKeyDown={handleEscapeKeyDown}
          onWheel={handleWheel}
          className={twMerge(rest.className, 'shadow-md')}
        >
          {children}
        </StyledContent>
      </Portal>
    )
  }
)

type PopoverContextType = {
  isOpen: boolean
}
const PopoverContext = React.createContext<PopoverContextType>({} as PopoverContextType)

const Popover = Object.assign(PopoverBase, {
  Anchor: PopoverPrimitive.Anchor,
  Arrow: PopoverArrow,
  Content: PopoverContent,
  Trigger: PopoverPrimitive.Trigger,
})

export default Popover
